import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Banner } from "@astryxdesign/core/Banner";
import { Button } from "@astryxdesign/core/Button";
import { VStack } from "@astryxdesign/core/Layout";
import { Text } from "@astryxdesign/core/Text";

import { isNative } from "../../native";
import { detectMobileOS, openInApp } from "../../lib/openInApp";

/**
 * 모바일 웹 → 앱 유도 배너 (2026-10-19).
 *
 * 검색·공유로 웹 카드/세트 페이지에 들어온 Android 사용자에게 "앱에서 열기"를 보여준다.
 * 누르면 지금 보는 경로 그대로 앱을 실행하고(intent://), 미설치면 Play 스토어로 간다
 * (install referrer 에 UTM — Play Console 획득 보고서에서 이 배너 몫이 따로 잡힌다).
 *
 * - Android 웹만: iOS 는 App Store 심사 통과 전이라 갈 곳이 없다. 출시되면 조건만 넓힌다
 * - 네이티브 앱·데스크탑에는 안 뜬다. 프리렌더(헤드리스 데스크탑 UA)에도 안 실린다
 * - 닫으면 DISMISS_DAYS 동안 다시 안 띄운다(브라우저 저장소 — 막혀 있으면 이번 화면만 닫힘)
 */
const DISMISS_KEY = "peeka.appInstallBanner.dismissedAt";
const DISMISS_DAYS = 14;
const PLAY_REFERRER = "utm_source=peeka_web&utm_medium=install_banner";

function recentlyDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return at > 0 && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function AppInstallBanner() {
  const { t } = useTranslation("common");
  const { pathname, search } = useLocation();
  const [visible, setVisible] = useState(
    () => !isNative() && detectMobileOS() === "android" && !recentlyDismissed(),
  );

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* 저장소 차단 — 이번 화면에서만 닫힌다 */
    }
  };

  return (
    <Banner
      status="info"
      title={t("appInstall.title")}
      isDismissable
      onDismiss={dismiss}
      /* 버튼을 endContent 에 두면 폰 폭에서 제목 칸이 4글자 폭으로 눌린다 — 설명 아래로 내린다 */
      description={
        <VStack gap={2} align="start">
          <Text>{t("appInstall.description")}</Text>
          <Button
            variant="primary"
            label={t("appInstall.open")}
            clickAction={() => {
              openInApp(`${pathname}${search}`, { playReferrer: PLAY_REFERRER });
            }}
          />
        </VStack>
      }
    />
  );
}
